<template>
  <section class="the-picture-editor__crop">
    <vue-cropper
      v-show="imgSrc"
      ref="cropper"
      :aspect-ratio="aspectRatio"
      :autoCropArea="1"
      :src="imgSrc"
      :imgStyle="imgStyle"
      :viewMode="1"
      alt="Source Image"
    ></vue-cropper>
    <div v-if="!imgSrc" class="croper-stab cropper-bg"></div>

    <div class="the-picture-editor__source__actions">
      <el-input
        v-model="rotateDeg"
        :disabled="!imgSrc"
        class="the-picture-editor__source__actions_item"
      >
        <el-button
          slot="append"
          icon="el-icon-refresh-right"
          :title="$t('picture_editor_rotate')"
          :disabled="!imgSrc"
          @click="rotate(rotateDeg)"
        ></el-button>
      </el-input>

      <el-button
        class="the-picture-editor__source__actions_item"
        :disabled="!imgSrc"
        @click="flipX"
      >
        {{ $t("picture_editor_flip_x") }}
      </el-button>

      <el-button
        class="the-picture-editor__source__actions_item"
        :disabled="!imgSrc"
        @click="flipY"
      >
        {{ $t("picture_editor_flip_y") }}
      </el-button>

      <el-button
        class="the-picture-editor__source__actions_item"
        icon="el-icon-zoom-in"
        :title="$t('picture_editor_zoom_in')"
        :disabled="!imgSrc"
        @click="zoom(0.2)"
      ></el-button>

      <el-button
        class="the-picture-editor__source__actions_item"
        icon="el-icon-zoom-out"
        :title="$t('picture_editor_zoom_out')"
        :disabled="!imgSrc"
        @click="zoom(-0.2)"
      ></el-button>

      <el-input-number
        class="the-picture-editor__source__actions_item"
        v-model="quality"
        :min="10"
        :max="100"
        :title="$t('picture_editor_params_quality')"
        :disabled="!imgSrc"
      ></el-input-number>

      <el-input
        class="the-picture-editor__source__actions_item"
        v-model="size"
        :title="$t('picture_editor_params_size')"
        readonly
      ></el-input>

      <el-button
        class="the-picture-editor__source__actions_item"
        :disabled="!imgSrc"
        @click="reset"
      >
        {{ $t("picture_editor_reset") }}
      </el-button>

      <el-button
        type="primary"
        class="the-picture-editor__source__actions_item"
        :disabled="!imgSrc"
        @click="preview"
      >
        {{ $t("picture_editor_params_preview") }}
      </el-button>
    </div>
  </section>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

import { mapMutations, mapState } from "vuex";

export default {
  name: "ThePictureEditorCrop",
  props: {
    height: {
      type: Number,
      default: 380
    },
    fileName: String
  },
  components: {
    VueCropper
  },
  data() {
    return {
      flipScaleX: 1,
      flipScaleY: 1,
      imgStyle: {
        height: `${this.height}px`
      }
    };
  },
  computed: {
    ...mapState({
      imgSrc: state => state.thePictureEditor.imgSrc,
      aspectRatio: state => state.thePictureEditor.aspectRatio,
      cropMe: state => state.thePictureEditor.cropMe,
      widthImgage: state => state.thePictureEditor.widthImgage,
      heightImage: state => state.thePictureEditor.heightImage
    }),
    rotateDeg: {
      get() {
        return this.$store.state.thePictureEditor.rotateDeg;
      },
      set(value) {
        this.updateRotateDeg(value);
      }
    },
    data: {
      get() {
        return this.data;
      },
      set(value) {
        this.updateData(value);
      }
    },
    quality: {
      get() {
        return this.$store.state.thePictureEditor.qualityImage;
      },
      set(value) {
        this.setQualityImage(value);
      }
    },
    size() {
      return `${this.widthImgage}x${this.heightImage}px`;
    }
  },
  methods: {
    ...mapMutations([
      "updateRotateDeg",
      "updateData",
      "setCropImage",
      "setQualityImage",
      "setFilePreview",
      "setCropMe"
    ]),
    getMimeType() {
      let arr = this.fileName.split(".");
      if (arr.length) {
        const mimeType = new Map();
        mimeType.set("jpeg", "image/jpeg");
        mimeType.set("jpg", "image/jpeg");
        mimeType.set("png", "image/png");

        const s = arr[arr.length - 1].toLowerCase();

        if (mimeType.has(s)) {
          return mimeType.get(s);
        }
      }

      return "";
    },
    cropImage() {
      this.setCropImage(
        this.$refs.cropper.getCroppedCanvas().toDataURL(this.getMimeType())
      );
    },
    flipX() {
      this.flipScaleX = this.flipScaleX * -1;
      this.$refs.cropper.scaleX(this.flipScaleX);
    },
    flipY() {
      this.flipScaleY = this.flipScaleY * -1;
      this.$refs.cropper.scaleY(this.flipScaleY);
    },
    getCropBoxData() {
      this.updateData(
        JSON.stringify(this.$refs.cropper.getCropBoxData(), null, 4)
      );
    },
    getData() {
      this.updateData(JSON.stringify(this.$refs.cropper.getData(), null, 4));
    },
    move(offsetX, offsetY) {
      this.$refs.cropper.move(offsetX, offsetY);
    },
    reset() {
      this.$refs.cropper.reset();
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
    },
    setCropBoxData() {
      if (!this.data) return;
      this.$refs.cropper.setCropBoxData(JSON.parse(this.data));
    },
    setData() {
      if (!this.data) return;
      this.$refs.cropper.setData(JSON.parse(this.data));
    },
    setImage(e) {
      const file = e.target.files[0];
      if (file.type.indexOf("image/") === -1) {
        alert("Please select an image file");
        return;
      }
      if (typeof FileReader === "function") {
        const reader = new FileReader();
        reader.onload = event => {
          this.imgSrc = event.target.result;
          // rebuild cropperjs with the updated source
          this.$refs.cropper.replace(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },
    showFileChooser() {
      this.$refs.input.click();
    },
    zoom(percent) {
      this.$refs.cropper.relativeZoom(percent);
    },
    preview() {
      this.setFilePreview("");
      this.cropImage();
      this.$emit("preview");
    },
    cropAfterForeignEvent(val) {
      if (val) {
        this.cropImage();
        this.setCropMe(false);
      }
    }
  },
  watch: {
    aspectRatio: function (val) {
      this.$refs.cropper.setAspectRatio(val);
    },
    imgSrc: function (val) {
      this.$refs.cropper.replace(val);
    },
    cropMe: function (val) {
      this.cropAfterForeignEvent(val);
    }
  }
};
</script>

<style lang="scss" scoped>
.the-picture-editor__crop {
  width: 100%;

  .croper-stab {
    width: 100%;
    height: 90%;
  }

  .the-picture-editor__source__actions {
    margin-top: 5px;

    .the-picture-editor__source__actions_item {
      max-width: 100px;
      margin-left: 10px;
    }
    .the-picture-editor__source__actions_item:first-child {
      margin-left: 0;
    }
  }
}
</style>
